/*
 * Cadwork webgl Engine
 * http://www.lexoview.com
 *
 * Copyright (C) 2015 Cadwork Informatik AG, Basel, Switzerland
 * Dual licensed under the MIT and GPL
 *
 * @author Jaroslav Přibyl (pribyl@cadwork.cz)
 *
 */

import {logInfo} from "App/debug";
import {FLOAT_PRECISION} from "App/math";
import {glMatrix, mat4, vec3} from "gl-matrix";

/**
 * This class is used to hold zero point offset that is added to local model coordinates.
 * Zeropoint offset is only show to user. It is not used in 3d transformations because of precision issues
 * (flickering, etc.).
 *
 * Data, computation and outputs of this class must be in double precision so private members are defined as Array of number
 * that is in 64bit instead of Float32Array (32 bit).
 */
export class Zeropoint {

    private _isSet: boolean = false;
    private _translationMat: mat4;
    private _rotation: number = 0.0;

    private _transformationToPlanetSpace: mat4;
    private _transformationToLocalSpace: mat4;

    private _pgc: {vec3Array: vec3[]};

    public constructor() {
        glMatrix.setMatrixArrayType(Array); // we need a 64bit precision here
        this._pgc = {
            vec3Array: [
                vec3.create(),
                vec3.create()
            ]
        };

        this._translationMat = mat4.create();
        this._transformationToPlanetSpace = mat4.create();
        this._transformationToLocalSpace  = mat4.create();
        glMatrix.setMatrixArrayType(Float32Array);
    }

    public isSet(): boolean {
        return this._isSet;
    }

    /**
     * Set zeropoint translation matrix and rotation
     * @param aTranslationMat {number[16]} 64bit precision number
     * @param aRotation {number} An angle in radians
     */
    public set(aTranslationMat: number[], aRotation: number): void {
        glMatrix.setMatrixArrayType(Array); // we need a 64bit precision here

        let isEmpty = this._isEmpty(aTranslationMat, aRotation);
        if (isEmpty) {
            return;
        }

        if (this._isSet && !isEmpty) {
            logInfo("A model with not empty zero point is added to scene which already has a zero point set by another model added to the scene earlier. This case is not yet handled! Please contact the developers to do so.");
            return;
        }

        mat4.copy(this._translationMat, aTranslationMat);

        this._rotation = aRotation;

        // create rotation mat
        let rotationMat = mat4.create();
        mat4.rotateY(rotationMat, rotationMat, this._rotation);

        mat4.multiply(this._transformationToPlanetSpace, rotationMat, this._translationMat);

        mat4.invert(this._transformationToLocalSpace, this._transformationToPlanetSpace);

        this._isSet = true;

        glMatrix.setMatrixArrayType(Float32Array); // back to 32 bit precision
    }

    // 64 bit
    public _transform(aLocalPoint: number[]): number[] {
        glMatrix.setMatrixArrayType(Array); // we need a 64bit precision here
        let planetPoint = this._pgc.vec3Array[0];
        let localPoint  = this._pgc.vec3Array[1];
        vec3.set(localPoint, aLocalPoint[0], aLocalPoint[1], -aLocalPoint[2]);
        vec3.transformMat4(planetPoint, localPoint, this._transformationToPlanetSpace);

        let result = vec3.clone(planetPoint);

        glMatrix.setMatrixArrayType(Float32Array); // back to 32 bit precision
        return result;
    }

    public _inverseTransform(aPlanetPoint: vec3): vec3 {
        glMatrix.setMatrixArrayType(Array); // we need a 64bit precision here
        // let localPoint = this._pgc.vec3Array[0];

        let localPoint = vec3.create();
        let planetPoint = vec3.fromValues(aPlanetPoint[0], aPlanetPoint[2], aPlanetPoint[1]);

        // vec3.set(localPoint, aLocalPoint[0], aLocalPoint[1], -aLocalPoint[2]);
        vec3.transformMat4(localPoint, planetPoint, this._transformationToLocalSpace);

        // vec3.set(localPoint, localPoint[0], localPoint[1], -localPoint[2]);

        let result = vec3.clone(localPoint);

        glMatrix.setMatrixArrayType(Float32Array); // back to 32 bit precision

        return result;
    }

    /**
     * Get zeropoint translation offset.
     */
    public getTranslationMat(): number[] {
        return mat4.clone(this._translationMat);
    }

    public getRotation(): number {
        return this._rotation;
    }

    public setRotation(aValue: number) {
        this._rotation = aValue;
    }

    private _isEmpty(aTranslationMat: mat4, aRotation: number): boolean {
        return !(Math.abs(aRotation) > FLOAT_PRECISION ||
            Math.abs(aTranslationMat[12]) > FLOAT_PRECISION ||
            Math.abs(aTranslationMat[13]) > FLOAT_PRECISION ||
            Math.abs(aTranslationMat[14]) > FLOAT_PRECISION);
    }
}
